import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { BoxGeometry, Euler, Mesh, Quaternion, Vector3 } from "three";
import {
  IntersectionEnterHandler,
  IntersectionEnterPayload,
  RapierRigidBody,
  RigidBody,
  RigidBodyAutoCollider,
  RigidBodyTypeString,
  useBeforePhysicsStep,
} from "@react-three/rapier";
import { usePlayerControls } from "./PlayerControls";
import { useFrame, useThree } from "@react-three/fiber";
import { degsToEuler, getObject3DBoundingBox } from "../../MeshUtils";
import { rayIntersects } from "./CollisionUtils";
import {
  CollisionObjectProps,
  CollisionShapeMap,
  CollisionType,
  CollisionTypeMap,
  OmitPhysicalContextsForControl,
  PhysicalContexts,
} from "./CollisionTypes";

const PLAYER_RAY_CAST_SPREAD = 0.75;
const HALF_PLAYER_RAY_CAST_SPREAD = PLAYER_RAY_CAST_SPREAD / 2;
const PLAYER_RAY_CAST_SPREAD_RATIO =
  PLAYER_RAY_CAST_SPREAD - HALF_PLAYER_RAY_CAST_SPREAD;
const PLAYER_DIAMOND_SIZE = 0.5;
const SPEED = 5;

export const CollisionObject: FC<CollisionObjectProps> = ({
  meshNode,
  isPlayer = false,
  mass = 1,
  collisionShape,
  collisionType = CollisionType.Static,
  collisionAttach = false,
  collisionMatchRotation = false,
  parentToCollision = false,
  friction = 0.5,
  sensor = false,
  restitution = 0,
  onPlayerCollide,
}) => {
  const correctedOrientation = useMemo<
    Pick<Mesh, "position" | "rotation" | "scale">
  >(
    () => ({
      // IMPORTANT: Don't initially offset the rigid body position when the collision object will be attached to it.
      position: collisionAttach
        ? new Vector3()
        : meshNode.localToWorld(new Vector3()),
      rotation: new Euler().setFromQuaternion(
        meshNode.getWorldQuaternion(new Quaternion())
      ),
      scale: meshNode.getWorldScale(new Vector3()),
    }),
    [meshNode, collisionAttach]
  );
  const targetCollisionShape: RigidBodyAutoCollider = useMemo(
    () => CollisionShapeMap[collisionShape],
    [collisionShape]
  );
  const targetCollisionType: RigidBodyTypeString = useMemo(
    () => CollisionTypeMap[collisionType],
    [collisionType]
  );
  const { meshClone, usingBounds } = useMemo(() => {
    let bounds = meshNode.clone(true),
      usingBounds = false;

    // IMPORTANT: Player needs collisionDiamond added automatically. Angles: 45,35,0
    if (isPlayer) {
      bounds = new Mesh(
        new BoxGeometry(
          PLAYER_DIAMOND_SIZE,
          PLAYER_DIAMOND_SIZE,
          PLAYER_DIAMOND_SIZE
        )
      );
      bounds.position.copy(meshNode.localToWorld(new Vector3()));
      bounds.rotation.copy(degsToEuler(45, 0, 45));
      bounds.scale.copy(meshNode.getWorldScale(new Vector3()));
      bounds.visible = false;
      usingBounds = true;
    }

    return {
      meshClone: bounds,
      usingBounds,
    };
  }, [meshNode, isPlayer]);
  const correctedCloneOrientation = useMemo<
    Pick<Mesh, "position" | "rotation" | "scale">
  >(
    () => ({
      // IMPORTANT: Don't initially offset the rigid body position when the collision object will be attached to it.
      position: collisionAttach
        ? new Vector3()
        : meshClone.localToWorld(new Vector3()),
      rotation: new Euler().setFromQuaternion(
        meshClone.getWorldQuaternion(new Quaternion())
      ),
      scale: meshClone.getWorldScale(new Vector3()),
    }),
    [meshClone, collisionAttach]
  );
  const correctedMeshValues = useMemo(
    () => (usingBounds ? correctedCloneOrientation : correctedOrientation),
    [usingBounds, correctedCloneOrientation, correctedOrientation]
  );
  const rbRef = useRef<RapierRigidBody>(null);
  const { playerStateRef, onFallEnd, onFallStart } =
    usePlayerControls(isPlayer);
  const collisionMeshRef = useRef<Mesh>(null);
  const boxArgs = useMemo(() => {
    const { x, y, z } =
      meshClone.geometry?.boundingBox?.getSize(new Vector3()) ||
      getObject3DBoundingBox(meshClone).getSize(new Vector3());

    return [x, y, z] as [number, number, number];
  }, [meshClone]);
  const sphereArgs = useMemo(() => {
    if (meshClone?.geometry?.boundingBox) {
      const { x, y, z } =
        meshClone.geometry?.boundingBox?.getSize(new Vector3()) ||
        getObject3DBoundingBox(meshClone).getSize(new Vector3());

      return [Math.max(x, y, z) / 2, 32, 32] as [number, number, number];
    } else {
      return [0, 0, 0] as [number, number, number];
    }
  }, [meshClone]);
  const onIntersectionEnter = useCallback<IntersectionEnterHandler>(
    (payload: IntersectionEnterPayload) => {
      if (sensor && onPlayerCollide) {
        const { other: { rigidBody } = {} } = payload;

        if ((rigidBody?.userData as any)?.isPlayer) {
          onPlayerCollide();
        }
      }
    },
    [sensor, onPlayerCollide, meshNode]
  );
  const { scene } = useThree();

  useBeforePhysicsStep(() => {
    const rb = rbRef.current;

    if (rb) {
      if (collisionAttach) {
        rb.setNextKinematicTranslation(meshNode.localToWorld(new Vector3()));
      }

      if (collisionMatchRotation) {
        const rotQuat = meshNode.getWorldQuaternion(new Quaternion());

        rb.setNextKinematicRotation(rotQuat);
      }

      if (isPlayer) {
        const direction = playerStateRef.current.direction;
        const linVel = rb.linvel();
        const { falling, airControlEnabled } = playerStateRef.current;
        const rayPos = new Vector3(
          rb.translation().x + Math.random() * PLAYER_RAY_CAST_SPREAD_RATIO,
          rb.translation().y,
          rb.translation().z + Math.random() * PLAYER_RAY_CAST_SPREAD_RATIO
        );
        const onGround = rayIntersects(
          rayPos,
          new Vector3(0, -1, 0),
          boxArgs[1] * 2 * 0.75,
          scene,
          PhysicalContexts,
          OmitPhysicalContextsForControl
        );
        const allowControl = airControlEnabled() || !falling;

        if (falling && onGround) {
          onFallEnd();
        } else if (!falling && !onGround) {
          onFallStart();
        }

        if (direction.toArray().some((x) => x !== 0)) {
          rb.setLinvel(
            new Vector3(
              direction.x !== 0 && allowControl
                ? direction.x * SPEED
                : linVel.x,
              direction.y !== 0 ? direction.y * SPEED : linVel.y,
              direction.z !== 0 && allowControl ? direction.z * SPEED : linVel.z
            ),
            true
          );
        }
      }
    }
  });

  useFrame((_rS, delta) => {
    const rb = rbRef.current;

    if (parentToCollision && rb) {
      if (collisionMeshRef.current) {
        const pos = new Vector3(
          rb.translation().x,
          rb.translation().y,
          rb.translation().z
        );
        const rotEl = new Euler().setFromQuaternion(
          collisionMeshRef.current.getWorldQuaternion(new Quaternion())
        );
        const scl = collisionMeshRef.current.getWorldScale(new Vector3());

        if (meshNode.parent) {
          meshNode.position.lerp(
            meshNode.parent.worldToLocal(pos),
            1 - delta * 10
          );
        } else {
          meshNode.position.copy(pos);
        }

        if (!usingBounds) {
          meshNode.quaternion.setFromEuler(rotEl);
          meshNode.scale.set(scl.x, scl.y, scl.z);
        }
      }
    }
  });

  useEffect(() => {
    // Hide the collision mesh representation.
    if (collisionMeshRef.current) {
      collisionMeshRef.current.visible = false;
    }

    if (collisionAttach && rbRef.current) {
      const rb = rbRef.current;

      rb.setTranslation(meshNode.localToWorld(new Vector3()), true);
    }

    if (rbRef.current) {
      const newUserData = {
        ...meshNode?.userData,
        ...(rbRef.current.userData as any),
        isPlayer: isPlayer,
      };

      rbRef.current.userData = newUserData as any;
    }
  }, [meshNode, isPlayer, collisionAttach]);

  return (
    <>
      <RigidBody
        ref={rbRef}
        mass={mass}
        colliders={targetCollisionShape}
        type={targetCollisionType}
        friction={friction}
        lockRotations={isPlayer}
        includeInvisible
        sensor={sensor}
        restitution={restitution}
        onIntersectionEnter={onIntersectionEnter}
        {...correctedMeshValues}
      >
        {targetCollisionShape === "trimesh" ? (
          <primitive
            ref={collisionMeshRef}
            object={meshClone}
            position={[0, 0, 0]}
            rotation={[0, 0, 0]}
            scale={[1, 1, 1]}
            opacity={0.5}
            visible={false}
          />
        ) : targetCollisionShape === "ball" ? (
          <mesh
            ref={collisionMeshRef}
            position={[0, 0, 0]}
            rotation={[0, 0, 0]}
            scale={[1, 1, 1]}
            visible={false}
          >
            <sphereGeometry args={sphereArgs} />
          </mesh>
        ) : (
          <mesh
            ref={collisionMeshRef}
            position={[0, 0, 0]}
            rotation={[0, 0, 0]}
            scale={[1, 1, 1]}
            visible={false}
          >
            <boxGeometry args={boxArgs} />
          </mesh>
        )}
      </RigidBody>
    </>
  );
};
